import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { type ActivityPeriod, activityPeriod } from '@/api/gamma-team/types';
import type { DateRange } from '@/design-system';
import { useUrlSyncedState } from '@/global-hooks/use-url-synced-state';

import { ALL_TEAM, DEFAULT_PERIOD, parseIsoDay, parsePeriod, toIsoDay } from './period';

export const TEAM_ACTIVITY_URL_KEYS = ['period', 'from', 'to', 'manager'] as const;

export const uniqueMemberIds = (raw: readonly string[]): string[] => [
  ...new Set(raw.filter((id) => id && id !== ALL_TEAM)),
];

interface UseTeamActivityFiltersArgs {
  canViewTeam: boolean;
  ownUserId: string | null;
}

export const useTeamActivityFilters = ({ canViewTeam, ownUserId }: UseTeamActivityFiltersArgs) => {
  const { state, setMany } = useUrlSyncedState(TEAM_ACTIVITY_URL_KEYS);
  const [, setSearchParams] = useSearchParams();

  const period = parsePeriod(state.period[0]);
  const from = useMemo(() => parseIsoDay(state.from[0]), [state.from]);
  const to = useMemo(() => parseIsoDay(state.to[0]), [state.to]);

  const requestedMembers = useMemo(() => uniqueMemberIds(state.manager), [state.manager]);
  const ownOnly = useMemo(() => (ownUserId ? [ownUserId] : null), [ownUserId]);
  const members = canViewTeam ? requestedMembers : ownOnly;

  const requestedIsOwn = requestedMembers.length === 1 && requestedMembers[0] === ownUserId;

  useEffect(() => {
    if (canViewTeam || !ownUserId || requestedIsOwn) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('manager');
        next.set('manager', ownUserId);
        return next;
      },
      { replace: true },
    );
  }, [canViewTeam, ownUserId, requestedIsOwn, setSearchParams]);

  const setPeriod = useCallback(
    (next: ActivityPeriod) => {
      const periodValue = next === DEFAULT_PERIOD ? undefined : next;
      if (next === activityPeriod.Custom) {
        setMany({ period: periodValue });
        return;
      }
      setMany({ period: periodValue, from: undefined, to: undefined });
    },
    [setMany],
  );

  const setRange = useCallback(
    (range: DateRange) =>
      setMany({
        period: activityPeriod.Custom,
        from: range.from ? toIsoDay(range.from) : undefined,
        to: range.to ? toIsoDay(range.to) : undefined,
      }),
    [setMany],
  );

  const setMembers = useCallback(
    (next: readonly string[]) => {
      if (!canViewTeam) return;
      setMany({ manager: uniqueMemberIds(next) });
    },
    [canViewTeam, setMany],
  );

  return { period, from, to, members, setPeriod, setRange, setMembers };
};
